// Pure helpers for the NSF terminliste page (routes/terminliste, components/nsfCompetition).
// No store access, no DOM: everything here is unit-tested in tests/lib/nsfCompetitionUi.test.js.

export const FILTER_KEYS = ['from', 'to', 'branchName', 'disciplineGroup', 'kretsName', 'distrikt', 'search', 'championship', 'includeCancelled', 'lat', 'lng', 'maxDistanceKm'];

const DEFAULTS = {
    from: '',
    to: '',
    branchName: '',
    disciplineGroup: '',
    kretsName: '',
    distrikt: '',
    search: '',
    championship: '',
    includeCancelled: 1,
    lat: '',
    lng: '',
    maxDistanceKm: '',
};

const RECENT_DAYS = 7;
const VIEWS = ['map', 'calendar'];
// Filters worth remembering between visits (dates and search are per visit; the «Nær meg»
// position stays so the radius keeps working).
const REMEMBER_KEYS = ['branchName', 'disciplineGroup', 'kretsName', 'distrikt', 'championship', 'includeCancelled', 'lat', 'lng', 'maxDistanceKm'];
export const REMEMBER_STORAGE_KEY = 'terminlisteQuery';
export const REMEMBER_SETTING_KEY = 'terminlisteQuery';
const NB_WEEKDAYS_LONG = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
// Same patterns as backend/lib/nsf-ics.js championshipFor.
const CHAMPIONSHIP_PATTERNS = [
    [/norgesmesterskap|\bNM\b/i, 'NM'],
    [/m\/DM og KM/i, 'DM+KM'],
    [/m\/DM\b/i, 'DM'],
    [/m\/KM\b/i, 'KM'],
];
const FALLBACK_COLOR = '#6c757d';
// One colour per NSF stevnetype (docs/Rubic/README.md lists the eight types). Blue is the home pin.
const TYPE_COLORS = {
    'Åpent stevne': '#198754',
    'Åpent stevne m/KM': '#20c997',
    'Åpent stevne m/DM': '#fd7e14',
    'Åpent stevne m/DM og KM': '#d63384',
    'Åpent stevne m/NC': '#6f42c1',
    Norgesmesterskap: '#dc3545',
    'Landsstevne for rekrutter': '#b8860b',
    'Internasjonalt stevne': '#0dcaf0',
};
const NB_MONTHS_SHORT = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
const OSLO_TZ = 'Europe/Oslo';

export function defaultFilters() {
    return { ...DEFAULTS };
}

/** Filters from a query string (with or without the leading «?»). Unknown keys are ignored. */
export function filtersFromQuery(search = '') {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const filters = { ...DEFAULTS };
    FILTER_KEYS.forEach((key) => {
        if (!params.has(key)) {
            return;
        }
        const value = params.get(key);
        if (key === 'includeCancelled') {
            filters.includeCancelled = value === '0' || value === 'false' ? 0 : 1;
        } else {
            filters[key] = value;
        }
    });
    return filters;
}

/** Query string with only the non-default filters, in FILTER_KEYS order, plus «view» when not the map. */
export function queryFromFilters(filters = {}, { view = 'map' } = {}) {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
        const value = filters[key];
        if (value === undefined || value === null || value === '' || String(value) === String(DEFAULTS[key])) {
            return;
        }
        params.set(key, String(value));
    });
    if (view && view !== 'map' && VIEWS.includes(view)) {
        params.set('view', view);
    }
    return params.toString();
}

/** 'map' | 'calendar' from the query string; anything else is the map. */
export function viewFromQuery(search = '') {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const view = params.get('view');
    return VIEWS.includes(view) ? view : 'map';
}

function withinDays(date, now, days) {
    if (!date) {
        return false;
    }
    const time = new Date(date).getTime();
    return Number.isFinite(time) && now.getTime() - time <= days * 24 * 60 * 60 * 1000 && time <= now.getTime();
}

/** 'cancelled' | 'new' | 'changed' | null — what the list shows next to a row. */
export function badgeFor(row, now = new Date()) {
    if (!row) {
        return null;
    }
    if (row.status >= 2) {
        return 'cancelled';
    }
    const log = Array.isArray(row.changeLog) ? row.changeLog : [];
    const recentChanges = log.filter(entry => entry && entry.type !== 'new' && entry.type !== 'other' && withinDays(entry.date, now, RECENT_DAYS));
    // «Ny» needs a real (non-backfill) «new» entry: after the first fill every row has a
    // fresh firstSeenDate, and badging the whole list would say nothing.
    const freshlyNew = log.some(entry => entry && entry.type === 'new' && !entry.backfill && withinDays(entry.date, now, RECENT_DAYS));
    if (freshlyNew && recentChanges.length === 0) {
        return 'new';
    }
    if (recentChanges.length > 0) {
        return 'changed';
    }
    return null;
}

/** «3. okt. 2026 10:00» in Oslo time; '' for missing/invalid dates. */
export function formatDateNo(value) {
    if (!value) {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: OSLO_TZ, year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = {};
    fmt.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
    return `${Number(parts.day)}. ${NB_MONTHS_SHORT[Number(parts.month) - 1]} ${parts.year} ${parts.hour}:${parts.minute}`;
}

function hasCoords(row) {
    return !!row && Number.isFinite(row.lat) && Number.isFinite(row.lng);
}

/** [[minLat, minLng], [maxLat, maxLng]] over rows with coordinates, or null. */
export function mapBounds(rows = []) {
    const points = rows.filter(hasCoords);
    if (points.length === 0) {
        return null;
    }
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;
    points.forEach((p) => {
        minLat = Math.min(minLat, p.lat);
        maxLat = Math.max(maxLat, p.lat);
        minLng = Math.min(minLng, p.lng);
        maxLng = Math.max(maxLng, p.lng);
    });
    return [[minLat, minLng], [maxLat, maxLng]];
}

/** Rows with coordinates grouped per exact position: [{ key, lat, lng, rows }] in first-seen order. */
export function groupByPosition(rows = []) {
    const groups = new Map();
    rows.forEach((row) => {
        if (!hasCoords(row)) {
            return;
        }
        const key = `${row.lat},${row.lng}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key, lat: row.lat, lng: row.lng, rows: [],
            });
        }
        groups.get(key).rows.push(row);
    });
    return [...groups.values()];
}

/**
 * Pixel offsets for n markers fanned out around one point: a circle (first marker at
 * the top) up to 12, a spiral beyond that. Same layout as the article feed map.
 */
export function fanOutOffsets(count) {
    const n = Math.max(0, Math.floor(count) || 0);
    const offsets = [];
    for (let i = 0; i < n; i += 1) {
        if (n <= 12) {
            let radius = 40;
            if (n > 10) {
                radius = 80;
            } else if (n > 5) {
                radius = 60;
            }
            const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
            offsets.push({ dx: radius * Math.cos(angle), dy: radius * Math.sin(angle) });
        } else {
            const angle = i * 0.8;
            const radius = 40 + i * 8;
            offsets.push({ dx: radius * Math.cos(angle), dy: radius * Math.sin(angle) });
        }
    }
    return offsets;
}

/** Marker/pill colour for a stevnetype name; grey for unknown or missing. */
export function colorForType(name) {
    return TYPE_COLORS[String(name || '').trim()] || FALLBACK_COLOR;
}

/** Stevnetype names in palette order (for a legend). */
export function knownTypes() {
    return Object.keys(TYPE_COLORS);
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

/** Local calendar date as YYYY-MM-DD (the <input type=date> value). */
export function localIsoDate(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function addDays(date, days) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() + days);
    return d;
}

/** { from, to } for a date shortcut chip: today | week (7 days) | month (30 days) | thisMonth; null if unknown. */
export function dateRangeShortcut(key, now = new Date()) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (key) {
    case 'today':
        return { from: localIsoDate(today), to: '' };
    case 'week':
        return { from: localIsoDate(today), to: localIsoDate(addDays(today, 7)) };
    case 'month':
        return { from: localIsoDate(today), to: localIsoDate(addDays(today, 30)) };
    case 'thisMonth':
        return monthBounds(`${today.getFullYear()}-${pad2(today.getMonth() + 1)}`);
    default:
        return null;
    }
}

function parseMonth(monthIso) {
    const m = /^(\d{4})-(\d{2})/.exec(String(monthIso || ''));
    if (!m) {
        return null;
    }
    return { year: Number(m[1]), month: Number(m[2]) - 1 };
}

/** First and last day of a YYYY-MM month as { from, to }. */
export function monthBounds(monthIso) {
    const p = parseMonth(monthIso);
    if (!p) {
        return { from: '', to: '' };
    }
    return {
        from: localIsoDate(new Date(p.year, p.month, 1)),
        to: localIsoDate(new Date(p.year, p.month + 1, 0)),
    };
}

/** YYYY-MM shifted by delta months. */
export function shiftMonth(monthIso, delta) {
    const p = parseMonth(monthIso);
    if (!p) {
        return '';
    }
    const d = new Date(p.year, p.month + delta, 1);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

/** YYYY-MM of a YYYY-MM-DD string ('' when empty). */
export function monthOf(iso) {
    const p = parseMonth(iso);
    return p ? `${p.year}-${pad2(p.month + 1)}` : '';
}

/** Monday-first weeks for a YYYY-MM month; each cell { iso, day, inMonth }. */
export function monthGrid(monthIso) {
    const p = parseMonth(monthIso);
    if (!p) {
        return [];
    }
    const first = new Date(p.year, p.month, 1);
    const last = new Date(p.year, p.month + 1, 0);
    const lead = (first.getDay() + 6) % 7;
    let cursor = addDays(first, -lead);
    const weeks = [];
    while (cursor <= last || weeks.length === 0 || (weeks[weeks.length - 1].length < 7)) {
        const week = [];
        for (let i = 0; i < 7; i += 1) {
            week.push({ iso: localIsoDate(cursor), day: cursor.getDate(), inMonth: cursor.getMonth() === p.month });
            cursor = addDays(cursor, 1);
        }
        weeks.push(week);
        if (cursor > last) {
            break;
        }
    }
    return weeks;
}

/** Oslo calendar date (YYYY-MM-DD) of a timestamp, '' when invalid. */
export function osloDateIso(value) {
    if (!value) {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: OSLO_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const parts = {};
    fmt.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
    return `${parts.year}-${parts.month}-${parts.day}`;
}

/** { 'YYYY-MM-DD': [rows] } keyed on the Oslo start date; rows without a date are dropped. */
export function rowsByDay(rows = []) {
    const byDay = {};
    rows.forEach((row) => {
        const iso = osloDateIso(row && row.startDate);
        if (!iso) {
            return;
        }
        if (!byDay[iso]) {
            byDay[iso] = [];
        }
        byDay[iso].push(row);
    });
    return byDay;
}

export function hasCoordinates(row) {
    return hasCoords(row);
}

/** 'NM' | 'DM' | 'KM' | 'DM+KM' | null from a stevnetype name. */
export function championshipFor(typeName) {
    const name = String(typeName || '');
    if (!name) {
        return null;
    }
    const hit = CHAMPIONSHIP_PATTERNS.find(([re]) => re.test(name));
    return hit ? hit[1] : null;
}

/** «Lørdag 31. okt.» for a YYYY-MM-DD string. */
export function dayHeaderNo(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) {
        return '';
    }
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return `${NB_WEEKDAYS_LONG[date.getDay()]} ${date.getDate()}. ${NB_MONTHS_SHORT[date.getMonth()]}`;
}

/** [{ iso, label, rows }] in date order (Oslo start date), for the day-grouped card list. */
export function groupRowsByDay(rows = []) {
    const byDay = rowsByDay(rows);
    return Object.keys(byDay).sort().map(iso => ({ iso, label: dayHeaderNo(iso), rows: byDay[iso] }));
}

/** The query string worth remembering between visits: sticky filters plus the view. */
export function rememberedQueryFor(filters = {}, view = 'map') {
    const subset = {};
    REMEMBER_KEYS.forEach((key) => {
        if (filters[key] !== undefined) {
            subset[key] = filters[key];
        }
    });
    return queryFromFilters(subset, { view });
}

/** Remembered query: the profile setting for a logged-in user, else local storage, else ''. */
export function readRememberedQuery({ user = null, storage = null } = {}) {
    const fromProfile = user && user.id && user.settings ? user.settings[REMEMBER_SETTING_KEY] : '';
    if (fromProfile) {
        return String(fromProfile);
    }
    if (storage && typeof storage.getItem === 'function') {
        try {
            return String(storage.getItem(REMEMBER_STORAGE_KEY) || '');
        } catch (e) {
            return '';
        }
    }
    return '';
}

/**
 * The profile query to apply once the user has loaded, or ''. Only on a page that was opened
 * without a query in the URL (a shared link wins), and only when it differs from what is shown.
 */
export function profileQueryToApply({ user = null, openedWithoutQuery = false, currentQuery = '' } = {}) {
    if (!openedWithoutQuery || !user || !user.id || !user.settings) {
        return '';
    }
    const stored = String(user.settings[REMEMBER_SETTING_KEY] || '');
    return stored && stored !== String(currentQuery || '') ? stored : '';
}

function stripSlash(url) {
    return String(url || '').replace(/\/+$/, '');
}

/** { feed, webcal, download } for the filtered terminliste feed. */
export function icsUrls({ apiServer = '', query = '' } = {}) {
    const base = `${stripSlash(apiServer)}/api/nsfcompetitions/terminliste.ics`;
    const feed = query ? `${base}?${query}` : base;
    return {
        feed,
        webcal: feed.replace(/^https?:\/\//, 'webcal://'),
        download: `${feed}${query ? '&' : '?'}download=1`,
    };
}

/** Download URL for one stevne as .ics. */
const NSF_APP_BASE = 'https://app.skyting.no/p/c/';

/** The stevne's public page in SkytterAdmin (details, registration), or '' without a Rubic GUID. */
export function nsfDetailsUrl(row = {}) {
    const guid = String(row.rubicId || '').trim();
    return /^[0-9a-f-]{36}$/i.test(guid) ? `${NSF_APP_BASE}${guid}/details` : '';
}

export function competitionIcsUrl({ apiServer = '', id } = {}) {
    return `${stripSlash(apiServer)}/api/nsfcompetitions/${id}.ics`;
}

const SAFE_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a'];
const BLOCK_AS_P = ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'blockquote', 'pre'];

function safeHref(attrs) {
    const m = /href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs || '');
    const href = (m && (m[1] || m[2] || m[3]) ? (m[1] || m[2] || m[3]) : '').trim();
    if (!/^(https?:\/\/|mailto:)/i.test(href)) {
        return '';
    }
    return href.replace(/"/g, '&quot;');
}

/**
 * Rubic descriptions are HTML written by the clubs. Keep simple formatting and
 * http(s)/mailto links; drop scripts, styles, attributes and every other tag
 * (their text stays). Plain text without tags gets <br> for its line breaks.
 */
export function sanitizeDescriptionHtml(html) {
    const text = String(html || '');
    if (!text) {
        return '';
    }
    if (!/<[a-zA-Z/!]/.test(text)) {
        return text.replace(/\r?\n/g, '<br>');
    }
    return text
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<(script|style|iframe|object|embed|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
        .replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (tag, rawName, attrs) => {
            const name = rawName.toLowerCase();
            const closing = tag.startsWith('</');
            if (name === 'br') {
                return closing ? '' : '<br>';
            }
            if (name === 'a') {
                if (closing) {
                    return '</a>';
                }
                const href = safeHref(attrs);
                return href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">` : '<a>';
            }
            if (SAFE_TAGS.includes(name)) {
                return closing ? `</${name}>` : `<${name}>`;
            }
            if (BLOCK_AS_P.includes(name)) {
                return closing ? '</p>' : '<p>';
            }
            return '';
        });
}

/** onClick for a «select this stevne» link: opens the drawer and keeps preact-router out of it. */
export function selectRowHandler(onSelect, row) {
    if (typeof onSelect !== 'function') {
        return undefined;
    }
    return (e) => {
        if (e) {
            e.preventDefault();
            // preact-router routes every <a href> click from a document listener and ignores
            // preventDefault, so the event must not reach the document.
            e.stopPropagation();
        }
        onSelect(row);
    };
}

const STATUS_NAMES_NB = ['Søknad', 'Godkjent', 'Avvist', 'Avlyst'];
const FIELD_LABELS_NB = {
    title: 'Tittel', status: 'Status', endDate: 'Slutt', facility: 'Sted',
};

function statusNameNb(value) {
    const n = Number(value);
    return STATUS_NAMES_NB[n] || String(value === null || value === undefined ? '' : value);
}

function fieldText(field) {
    if (field.key === 'position') {
        return 'Kartposisjon oppdatert';
    }
    const label = FIELD_LABELS_NB[field.key] || field.key;
    if (field.key === 'status') {
        return `${label}: ${statusNameNb(field.from)} → ${statusNameNb(field.to)}`;
    }
    if (field.key === 'endDate') {
        return `${label}: ${formatDateNo(field.to) || '–'} (var ${formatDateNo(field.from) || '–'})`;
    }
    return `${label}: ${field.from || '–'} → ${field.to || '–'}`;
}

/** One readable line (Norwegian) for a changeLog entry; '' when the entry carries no detail. */
export function describeChange(entry) {
    if (!entry) {
        return '';
    }
    switch (entry.type) {
    case 'dateMoved':
        return `Ny dato: ${formatDateNo(entry.to) || '–'} (var ${formatDateNo(entry.from) || '–'})`;
    case 'cancelled':
        return `${statusNameNb(entry.to)} (var ${statusNameNb(entry.from)})`;
    case 'venueChanged':
        return `Nytt sted: ${entry.to || '–'} (var ${entry.from || '–'})`;
    case 'other':
        return Array.isArray(entry.fields) ? entry.fields.map(fieldText).join(' · ') : '';
    default:
        return '';
    }
}
